/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined, CalendarOutlined } from '@ant-design/icons'
import { Modal, DatePicker, Input } from 'antd'
import type { Dayjs } from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
} from 'src/components/css'

interface ByeWeeksModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: ByeWeekFormData) => void
  loading?: boolean
}

export interface ByeWeekFormData {
  startDate: Dayjs | null
  endDate: Dayjs | null
  description: string
}

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  min-height: 400px;
`

const rowStyles = css`
  display: flex;
  gap: 16px;
  width: 100%;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const fieldContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const labelStyles = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
  margin: 0;
`

const inputBoxStyles = css`
  background-color: #313b4b;
  border: 1px solid #3a4659;
  border-radius: 8px;
  min-height: 40px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${paragraphMedium}
  ${weightRegular}
  color: ${colors.WHITE};

  &:hover {
    border-color: #3a4659;
  }

  &:focus,
  &:focus-within {
    border-color: ${colors.PRIMARY_300};
    box-shadow: none;
  }
`

const datePickerStyles = css`
  &.ant-picker {
    ${inputBoxStyles}
    width: 100%;

    .ant-picker-input {
      input {
        ${paragraphMedium}
        ${weightRegular}
        color: ${colors.WHITE};

        &::placeholder {
          color: ${colors.NEUTRAL_100};
        }
      }
    }

    .ant-picker-suffix {
      color: ${colors.WHITE};
      font-size: 18px;
    }

    .ant-picker-clear {
      background: #313b4b;
      color: ${colors.WHITE};
    }
  }
`

const textAreaStyles = css`
  &.ant-input {
    ${inputBoxStyles}
    min-height: 80px;
    resize: none;

    &::placeholder {
      color: ${colors.NEUTRAL_100};
    }
  }
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
  gap: 16px;
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  flex: 1;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const confirmButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_400};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ByeWeeksModal: React.FC<ByeWeeksModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [endDate, setEndDate] = useState<Dayjs | null>(null)
  const [description, setDescription] = useState('')

  const resetForm = useCallback(() => {
    setStartDate(null)
    setEndDate(null)
    setDescription('')
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleConfirm = useCallback(() => {
    if (!startDate || !endDate) {
      return
    }
    onConfirm({
      startDate,
      endDate,
      description,
    })
    resetForm()
  }, [onConfirm, startDate, endDate, description, resetForm])

  const isValid = startDate && endDate

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>Add Bye Week</h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={handleClose}
          aria-label='Close modal'
          disabled={loading}
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Form */}
      <div css={formStyles}>
        {/* Start Date and End Date Row */}
        <div css={rowStyles}>
          <div css={fieldContainerStyles}>
            <label css={labelStyles}>Start Date</label>
            <DatePicker
              css={datePickerStyles}
              value={startDate}
              onChange={(date) => setStartDate(date)}
              format='MMM DD, YYYY'
              placeholder='Select date'
              suffixIcon={<CalendarOutlined />}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              disabled={loading}
            />
          </div>
          <div css={fieldContainerStyles}>
            <label css={labelStyles}>End Date</label>
            <DatePicker
              css={datePickerStyles}
              value={endDate}
              onChange={(date) => setEndDate(date)}
              format='MMM DD, YYYY'
              placeholder='Select date'
              suffixIcon={<CalendarOutlined />}
              disabledDate={(current) =>
                startDate
                  ? current && current.isBefore(startDate, 'day')
                  : false
              }
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              disabled={loading}
            />
          </div>
        </div>

        {/* Description Row */}
        <div css={fieldContainerStyles}>
          <label css={labelStyles}>Description</label>
          <Input.TextArea
            css={textAreaStyles}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Enter description...'
            rows={2}
            disabled={loading}
          />
        </div>
      </div>

      {/* Footer */}
      <div css={footerStyles}>
        <div css={buttonContainerStyles}>
          <button
            type='button'
            css={cancelButtonStyles}
            onClick={handleClose}
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            type='button'
            css={confirmButtonStyles}
            onClick={handleConfirm}
            disabled={!isValid || loading}
          >
            {loading ? 'SAVING...' : 'CONFIRM'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ByeWeeksModal
